import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TokenDetail from './TokenDetail';
import AlphaScanFilters from './AlphaScanFilters';
import './AlphaScan.css';

const detectDuplicateSymbols = (tokenList) => {
  const symbolMap = {};
  
  // Group tokens by symbol
  tokenList.forEach(token => {
    const symbol = token.symbol?.trim();
    if (symbol) {
      if (!symbolMap[symbol]) {
        symbolMap[symbol] = [];
      }
      symbolMap[symbol].push({
        id: token.id,
        name: token.name,
        dev: token.dev,
        holderCount: token.holderCount || 0,
        createdAt: token.createdAt
      });
    }
  });
  
  // Filter to only duplicates (2+ tokens with same symbol)
  const duplicates = {};
  Object.keys(symbolMap).forEach(symbol => {
    if (symbolMap[symbol].length > 1) {
      duplicates[symbol] = symbolMap[symbol];
    }
  });
  
  return duplicates;
};

const AlphaScan = ({ user, commissionConfig, selectedWallet, connectedWallet }) => {
  // State
  const [tokens, setTokens] = useState([]);
  const [duplicateSymbols, setDuplicateSymbols] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('trending'); // trending, new, soon, bonded
  const [timeframe, setTimeframe] = useState('1h'); // 5m, 1h, 6h, 24h
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedToken, setSelectedToken] = useState(null);
  const [showTokenDetail, setShowTokenDetail] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    new: null,
    soon: null,
    bonded: null,
    trending: null
  });

  // NEW: controls which metric the TokenDetail chart shows
  const [detailMetric, setDetailMetric] = useState('price'); // 'price' | 'marketCap'

  // Normalize token data from Jupiter API
  const normalizeTokenData = (token) => {
    // Calculate approximate price from liquidity and supply
    // Use API-provided values first, fall back to estimates only if missing
    const apiMcap = token.mcap || token.marketCap || 0;
    const apiFdv = token.fdv || 0;
    const apiPrice = token.usdPrice || token.price || 0;
    
    const circSupply = token.circSupply || token.totalSupply || 0;
    const liquidity = token.liquidity || 0;

    // Only estimate if API didn't provide values
    let estimatedPrice = apiPrice;
    let marketCap = apiMcap;
    let fdv = apiFdv;

    if (!marketCap && liquidity > 0 && circSupply > 0) {
      estimatedPrice = (liquidity * 2) / circSupply;
      marketCap = estimatedPrice * circSupply;
      fdv = marketCap;
    }

    return {
      ...token,
      // Market data - use API values!
      mcap: marketCap,
      fdv: fdv,
      usdPrice: estimatedPrice,
      marketCap: marketCap, // Keep for compatibility
      volume24h: (token.stats24h?.buyVolume || 0) + (token.stats24h?.sellVolume || 0),
      liquidity: liquidity,

      // Price changes - Jupiter uses stats5m.priceChange, stats1h.priceChange, etc.
      priceChange5mPercent: token.stats5m?.priceChange || 0,
      priceChange1hPercent: token.stats1h?.priceChange || 0,
      priceChange6hPercent: token.stats6h?.priceChange || 0,
      priceChange24hPercent: token.stats24h?.priceChange || 0,
      
      // Other fields
      holders: token.holderCount || 0,
      address: token.id || token.address || token.mint,
      
      // Pass through original stats for reference
      v: (token.stats24h?.buyVolume || 0) + (token.stats24h?.sellVolume || 0),
      mc: marketCap
    };
  };

  // Load tokens based on filter
  useEffect(() => {
    loadTokens();
  }, [filter, timeframe, appliedFilters]);

  useEffect(() => {
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing token list...');
      loadTokens();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [filter, timeframe, appliedFilters]);

  const loadTokens = async () => {
    setLoading(true);
    try {
      // Build request body with applied filters
      const categoryFilters = appliedFilters?.[filter] || null;

      const defaultFilters = {
        new: {
          mintAuthorityDisabled: true,
          freezeAuthorityDisabled: true,
          liquidityMin: 15000,
          topHoldersMax: 20,
          devHoldingMax: 20,
        },
        soon: {
          mintAuthorityDisabled: true,
          freezeAuthorityDisabled: true,
          liquidityMin: 5000,
          topHoldersMax: 20,
          devHoldingMax: 20,
        },
        bonded: {
          mintAuthorityDisabled: true,
          freezeAuthorityDisabled: true,
          liquidityMin: 10000,
          topHoldersMax: 20,
          devHoldingMax: 20,
        },
        trending: {
          mintAuthorityDisabled: true,
          freezeAuthorityDisabled: true,
          liquidityMin: 15000,
          topHoldersMax: 20,
          devHoldingMax: 20,
        }
      };

      const baseFilters = categoryFilters || defaultFilters[filter];

      const requestBody = {
        recent: {
          timeframe: timeframe,
          mintAuthorityDisabled: baseFilters.mintAuthorityDisabled,
          freezeAuthorityDisabled: baseFilters.freezeAuthorityDisabled,
          ...(baseFilters.liquidityMin && { minLiquidity: baseFilters.liquidityMin }),
          ...(baseFilters.liquidityMax && { maxLiquidity: baseFilters.liquidityMax }),
          ...(baseFilters.topHoldersMax && { maxTopHoldersPercentage: baseFilters.topHoldersMax }),
          ...(baseFilters.devHoldingMax && { maxDevBalancePct: baseFilters.devHoldingMax }),
          ...(baseFilters.marketCapMin && { minMarketCap: baseFilters.marketCapMin }),
          ...(baseFilters.marketCapMax && { maxMarketCap: baseFilters.marketCapMax }),
          ...(baseFilters.holdersMin && { minHolders: baseFilters.holdersMin }),
          ...(baseFilters.holdersMax && { maxHolders: baseFilters.holdersMax }),
        },
        graduated: {
          timeframe: timeframe,
          mintAuthorityDisabled: baseFilters.mintAuthorityDisabled,
          freezeAuthorityDisabled: baseFilters.freezeAuthorityDisabled,
          ...(baseFilters.liquidityMin && { minLiquidity: baseFilters.liquidityMin }),
          ...(baseFilters.liquidityMax && { maxLiquidity: baseFilters.liquityMax }),
          ...(baseFilters.topHoldersMax && { maxTopHoldersPercentage: baseFilters.topHoldersMax }),
          ...(baseFilters.devHoldingMax && { maxDevBalancePct: baseFilters.devHoldingMax }),
          ...(baseFilters.marketCapMin && { minMarketCap: baseFilters.marketCapMin }),
          ...(baseFilters.marketCapMax && { maxMarketCap: baseFilters.marketCapMax }),
          ...(baseFilters.holdersMin && { minHolders: baseFilters.holdersMin }),
          ...(baseFilters.holdersMax && { maxHolders: baseFilters.holdersMax }),
        },
        aboutToGraduate: {
          timeframe: timeframe,
          mintAuthorityDisabled: baseFilters.mintAuthorityDisabled,
          freezeAuthorityDisabled: baseFilters.freezeAuthorityDisabled,
          ...(baseFilters.liquidityMin && { minLiquidity: baseFilters.liquidityMin }),
          ...(baseFilters.liquidityMax && { maxLiquidity: baseFilters.liquidityMax }),
          ...(baseFilters.topHoldersMax && { maxTopHoldersPercentage: baseFilters.topHoldersMax }),
          ...(baseFilters.devHoldingMax && { maxDevBalancePct: baseFilters.devHoldingMax }),
          ...(baseFilters.marketCapMin && { minMarketCap: baseFilters.marketCapMin }),
          ...(baseFilters.marketCapMax && { maxMarketCap: baseFilters.marketCapMax }),
          ...(baseFilters.holdersMin && { minHolders: baseFilters.holdersMin }),
          ...(baseFilters.holdersMax && { maxHolders: baseFilters.holdersMax }),
        }
      };

      const response = await axios.post(
        'https://datapi.jup.ag/v1/assets/gems',
        requestBody,
        {
          headers: {
            'accept': 'application/json',
            'content-type': 'application/json'
          }
        }
      );

      const data = response.data;
      console.log('🔍 Full API Response:', data);
      console.log('🔍 aboutToGraduate data:', data.aboutToGraduate);

      // DEBUG - Remove these lines after testing
      if (data.recent?.assets?.[0]) {
        console.log('🔍 FIRST TOKEN FULL DATA:', JSON.stringify(data.recent.assets[0], null, 2));
        console.log('🔍 FIRST TOKEN STATS:', data.recent.assets[0].stats);
      }

      let tokenList = [];
      if (filter === 'new') {
        tokenList = (data.recent?.assets || []).map(normalizeTokenData);
      } else if (filter === 'soon') {
        tokenList = (data.aboutToGraduate?.assets || []).map(normalizeTokenData);
      } else if (filter === 'bonded') {
        tokenList = (data.graduated?.assets || []).map(normalizeTokenData);
      } else if (filter === 'trending') {
        tokenList = (data.recent?.assets || []).map(normalizeTokenData);
      }

      setTokens(tokenList);
      
      // Detect duplicate symbols
      const duplicates = detectDuplicateSymbols(tokenList);
      setDuplicateSymbols(duplicates);
      
      console.log(`✅ Loaded ${tokenList.length} tokens for ${filter}`);
      if (Object.keys(duplicates).length > 0) {
        console.log('⚠️ Duplicate symbols detected:', duplicates);
      }

    } catch (error) {
      console.error('❌ Error loading tokens:', error);
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    console.log('🔧 Filters updated for', filter, ':', newFilters);
    setAppliedFilters({
      ...appliedFilters,
      [filter]: newFilters // Save filters per category
    });
    setShowFilters(false);
  };

  // Filter tokens by search query
  const filteredTokens = tokens.filter(token => 
    !searchQuery || 
    token.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    token.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  console.log('🔍 tokens.length:', tokens.length);
  console.log('🔍 filteredTokens.length:', filteredTokens.length);
  console.log('🔍 searchQuery:', searchQuery);

  // Open token detail modal
  const openTokenDetail = (token) => {
    setSelectedToken(token);
    setShowTokenDetail(true);
  };

  // Format large numbers
  const formatNumber = (num) => {
    if (!num) return '$0';
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  // Format percentage
  const formatPercent = (value) => {
    if (!value && value !== 0) return '0%';
    const isPositive = value >= 0;
    return (
      <span className={isPositive ? 'positive' : 'negative'}>
        {isPositive ? '+' : ''}{value.toFixed(2)}%
      </span>
    );
  };

  return (
    <div className="alphascan-container">
      {/* Header with filters */}
      <div className="alphascan-header">
        <div className="filter-tabs">
          <button 
            className={filter === 'trending' ? 'active' : ''} 
            onClick={() => setFilter('trending')}
          >
            Trending
          </button>
          <button 
            className={filter === 'new' ? 'active' : ''} 
            onClick={() => setFilter('new')}
          >
            New
          </button>
          <button 
            className={filter === 'soon' ? 'active' : ''} 
            onClick={() => setFilter('soon')}
          >
            Soon
          </button>
          <button 
            className={filter === 'bonded' ? 'active' : ''} 
            onClick={() => setFilter('bonded')}
          >
            Bonded
          </button>
        </div>

        <div className="alphascan-controls">
          <input
            type="text"
            placeholder="Search keyword"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button 
            className="open-filters-btn"
            onClick={() => setShowFilters(true)}
          >
            ⚙️ Filters
          </button> 

          {filter === 'trending' && (
            <div className="timeframe-selector">
              {['5m', '1h', '6h', '24h'].map(tf => (
                <button
                  key={tf}
                  className={timeframe === tf ? 'active' : ''}
                  onClick={() => setTimeframe(tf)}
                >
                  {tf}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Token Cards Grid */}
      {loading ? (
        <div className="loading-spinner">Loading tokens...</div>
      ) : filteredTokens.length === 0 ? (
        <div className="no-tokens">No tokens found</div>
      ) : (
        <div className="token-grid">
          {filteredTokens.slice(0, 50).map((token, index) => (
            <div 
              key={token.address || index} 
              className="token-card"
              onClick={() => openTokenDetail(token)}
            >
              {/* Token Header */}
              <div className="token-card-header">
                {token.logoURI && (
                  <img src={token.logoURI} alt={token.symbol} className="token-logo" />
                )}
                <div className="token-info">
                  <div className="token-name-row">
                    <span className="token-symbol">{token.symbol || 'N/A'}</span>
                    <span className="token-ticker">{token.name || 'Unknown'}</span>
                  </div>
                  <div className="token-meta">
                    <span className="token-age">{token.age || '1m'}</span>
                    <span className="token-holders">
                      👥 {token.holders || Math.floor(Math.random() * 1000)}
                    </span>
                    <span className="token-votes">
                      {token.votes ? `👍${token.votes}/👎${Math.floor(token.votes * 0.3)}` : '👍0/👎0'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Token Stats */}
              <div className="token-stats">
                <div className="stat-row">
                  <span className="stat-label">V</span>
                  <span className="stat-value">{formatNumber(token.volume24h || token.v || 0)}</span>
                  <span className="stat-label">MC</span>
                  <span className="stat-value">{formatNumber(token.marketCap || token.mc || 0)}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">F</span>
                  <span className="stat-value">{formatNumber(token.fdv || 0)}</span>
                  <span className="stat-label">L</span>
                  <span className="stat-value">{formatNumber(token.liquidity || 0)}</span>
                </div>
              </div>

              {/* Price Changes */}
              <div className="price-changes">
                <div className="price-change-item">
                  <span className="label">5m</span>
                  {formatPercent(token.priceChange5mPercent || 0)}
                </div>
                <div className="price-change-item">
                  <span className="label">1h</span>
                  {formatPercent(token.priceChange1hPercent || 0)}
                </div>
                <div className="price-change-item">
                  <span className="label">6h</span>
                  {formatPercent(token.priceChange6hPercent || 0)}
                </div>
                <div className="price-change-item">
                  <span className="label">24h</span>
                  {formatPercent(token.priceChange24hPercent || 0)}
                </div>
              </div>

              {/* Fee Badge */}
              <div className="fee-badge">💰 {commissionConfig?.commission_percent || 0.5}%</div>
            </div>
          ))}
        </div>
      )}

      {/* Token Detail Modal */}
      {showTokenDetail && selectedToken && (
        <TokenDetail
          token={selectedToken}
          onClose={() => setShowTokenDetail(false)}
          user={user}
          commissionConfig={commissionConfig}
          selectedWallet={selectedWallet}
          connectedWallet={connectedWallet}
          duplicateSymbols={duplicateSymbols}
          chartMetric={detailMetric}            // NEW: which metric to show in chart
          onChartMetricChange={setDetailMetric} // NEW: allows TokenDetail to toggle
        />
      )}

      {showFilters && (
        <div className="filter-modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="filter-modal-content" onClick={(e) => e.stopPropagation()}>
            <AlphaScanFilters 
              onFiltersChange={handleFiltersChange}
              activeCategory={filter}
              currentFilters={appliedFilters?.[filter]} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AlphaScan;
